import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import ReactMarkdown from "react-markdown";
import {
  AlertTriangle,
  ArrowLeft,
  Compass,
  Copy,
  Lightbulb,
  Menu,
  MessageSquare,
  Newspaper,
  Pencil,
  Plus,
  Send,
  Settings as SettingsIcon,
  Shield,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  chat as chatApi,
  chatPersonas as personasApi,
  chatThreads as threadsApi,
  type ChatPersona,
  type ChatResponse,
  type ChatThread,
} from "@/lib/api";
import { getDomainHints } from "@/lib/domain-hints";
import { useWorkspaces } from "@/store/workspaces";

interface ChatPanelProps {
  workspaceId?: string;
}

interface UiTurn {
  role: "user" | "assistant";
  content: string;
  intent?: ChatResponse["intent"];
  meta?: string;
  exportUrl?: string | null;
}

type MenuView = "main" | "history" | "personas" | "persona-edit" | "settings";

export function ChatPanel({ workspaceId }: ChatPanelProps) {
  const [input, setInput] = useState("");
  const [history, setHistory] = useState<UiTurn[]>([]);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [activePersonaId, setActivePersonaId] = useState<string | null>(null);
  const [menuView, setMenuView] = useState<MenuView | null>(null);
  const [editingPersona, setEditingPersona] = useState<ChatPersona | null>(null);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { getById } = useWorkspaces();
  const ws = workspaceId ? getById(workspaceId) : undefined;
  const domainStarters = getDomainHints(ws?.domain).chatStarters;

  // Threads + personas listings (only fetched when menu opens)
  const threadsQ = useQuery({
    queryKey: ["chat-threads", workspaceId],
    queryFn: () => threadsApi.list(workspaceId!),
    enabled: Boolean(workspaceId && menuView),
  });
  const personasQ = useQuery({
    queryKey: ["chat-personas", workspaceId],
    queryFn: () => personasApi.list(workspaceId!),
    enabled: Boolean(workspaceId),
  });

  const activePersona = useMemo(
    () => (personasQ.data ?? []).find((p) => p.id === activePersonaId) ?? null,
    [personasQ.data, activePersonaId],
  );

  // Show persona-defined starters when one is picked; else domain defaults.
  const starterSuggestions = activePersona?.starter_prompts?.length
    ? activePersona.starter_prompts
    : domainStarters;

  const send = useMutation({
    mutationFn: (message: string) => {
      if (!workspaceId) throw new Error("no workspace selected");
      return chatApi.send(workspaceId, {
        message,
        history: history.map(({ role, content }) => ({ role, content })),
        thread_id: threadId,
        persona_id: activePersonaId,
      });
    },
    onSuccess: (res, message) => {
      setHistory((prev) => [
        ...prev,
        { role: "user", content: message },
        {
          role: "assistant",
          content: res.reply,
          intent: res.intent,
          meta: summarize(res),
          exportUrl: res.export_url,
        },
      ]);
      // The backend creates / resolves the thread; pin it for follow-up turns.
      if (res.thread_id) setThreadId(res.thread_id);
      qc.invalidateQueries({ queryKey: ["chat-threads", workspaceId] });

      const mutated =
        res.created_entity_ids.length ||
        res.created_relationship_ids.length ||
        res.created_signal_ids.length ||
        res.created_scenario_ids.length ||
        res.merged_entity_pairs.length ||
        res.recolored_entity_ids.length ||
        res.areas_set_entity_ids.length ||
        res.deleted_entity_ids.length ||
        res.deleted_signal_ids?.length;
      if (mutated) {
        qc.invalidateQueries({ queryKey: ["entities", workspaceId] });
        qc.invalidateQueries({ queryKey: ["relationships", workspaceId] });
        qc.invalidateQueries({ queryKey: ["signals", workspaceId] });
        qc.invalidateQueries({ queryKey: ["scenarios", workspaceId] });
      }
      if (res.focus_entity_ids.length) {
        navigate({
          to: ".",
          search: (prev: Record<string, unknown>) => ({
            ...prev,
            focus: res.focus_entity_ids.join(","),
          }),
        });
      }
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const message = input.trim();
    if (!message || send.isPending || !workspaceId) return;
    setInput("");
    send.mutate(message);
  };

  const openThread = async (t: ChatThread) => {
    if (!workspaceId) return;
    const msgs = await threadsApi.messages(workspaceId, t.id);
    setHistory(
      msgs.map((m) => ({
        role: m.role,
        content: m.content,
        intent: m.meta?.intent as ChatResponse["intent"] | undefined,
      })),
    );
    setThreadId(t.id);
    setActivePersonaId(t.persona_id ?? null);
    setMenuView(null);
  };

  const newChat = () => {
    setHistory([]);
    setThreadId(null);
    setMenuView(null);
  };

  const deleteThread = useMutation({
    mutationFn: (tid: string) => threadsApi.remove(workspaceId!, tid),
    onSuccess: (_, tid) => {
      qc.invalidateQueries({ queryKey: ["chat-threads", workspaceId] });
      if (threadId === tid) newChat();
    },
  });

  // Auto-scroll the conversation to the bottom on new content
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [history.length, send.isPending]);

  return (
    <div className="relative flex h-full flex-col panel overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMenuView((v) => (v ? null : "main"))}
            className="grid h-7 w-7 place-items-center rounded text-muted-foreground hover:text-foreground hover:bg-surface-elevated transition"
            title="Menu"
          >
            <Menu className="h-3.5 w-3.5" />
          </button>
          <span className="text-[13px] font-semibold tracking-tight">
            {activePersona ? activePersona.name : "Chat"}
          </span>
          {activePersona && (
            <button
              onClick={() => setActivePersonaId(null)}
              className="text-muted-foreground hover:text-foreground"
              title="Clear lens"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1 text-muted-foreground">
          <button
            onClick={newChat}
            className="grid h-7 w-7 place-items-center rounded hover:text-foreground hover:bg-surface-elevated"
            title="New chat"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Active persona hint bar */}
      {activePersona?.description && (
        <div className="px-3 py-1.5 border-b border-border bg-surface-elevated/30 text-[10px] text-muted-foreground italic">
          {activePersona.description}
        </div>
      )}

      {/* Conversation */}
      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-auto scrollbar-thin p-4 space-y-4 text-[13px]"
      >
        {!workspaceId && (
          <div className="rounded-md border border-risk-medium/30 bg-risk-medium/10 px-3 py-2 text-[12px] text-risk-medium flex items-start gap-2">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>Open a workspace to start chatting.</span>
          </div>
        )}

        {history.length === 0 && workspaceId && (
          <div className="space-y-1.5">
            <p className="text-muted-foreground text-[12px] mb-2">
              Try one of these to get started:
            </p>
            {starterSuggestions.map((s) => (
              <button
                key={s}
                onClick={() => setInput(s)}
                className="w-full text-left rounded-md border border-border bg-surface-elevated/50 px-3 py-2 text-[12px] text-foreground/85 hover:border-primary/40 hover:text-foreground"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {history.map((t, i) =>
          t.role === "user" ? (
            <div
              key={i}
              className="rounded-md bg-primary/15 border border-primary/25 px-3 py-2.5 text-foreground/95 ml-auto max-w-[90%]"
            >
              {t.content}
            </div>
          ) : (
            <div key={i}>
              <div className="text-foreground/95 chat-markdown">
                <ReactMarkdown
                  components={{
                    p: ({ children }) => <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>,
                    strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
                    em: ({ children }) => <em className="italic">{children}</em>,
                    ul: ({ children }) => <ul className="list-disc pl-5 mb-2 space-y-0.5">{children}</ul>,
                    ol: ({ children }) => <ol className="list-decimal pl-5 mb-2 space-y-0.5">{children}</ol>,
                    li: ({ children }) => <li className="leading-relaxed">{children}</li>,
                    h1: ({ children }) => <h3 className="text-[14px] font-semibold mt-2 mb-1">{children}</h3>,
                    h2: ({ children }) => <h3 className="text-[13px] font-semibold mt-2 mb-1">{children}</h3>,
                    h3: ({ children }) => <h3 className="text-[12px] font-semibold mt-2 mb-1 uppercase tracking-wider">{children}</h3>,
                    code: ({ children, className }) =>
                      className ? (
                        <pre className="rounded bg-surface-elevated/60 border border-border px-2 py-1.5 my-2 overflow-auto text-[11px]"><code>{children}</code></pre>
                      ) : (
                        <code className="rounded bg-surface-elevated/60 px-1 py-0.5 text-[12px] font-mono">{children}</code>
                      ),
                    a: ({ children, href }) => (
                      <a href={href} target="_blank" rel="noreferrer" className="text-primary underline underline-offset-2">
                        {children}
                      </a>
                    ),
                  }}
                >
                  {t.content}
                </ReactMarkdown>
              </div>
              {t.meta && (
                <p className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                  {t.meta}
                </p>
              )}
              {t.exportUrl && (
                <a
                  href={t.exportUrl}
                  download
                  className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/10 text-primary px-2.5 py-1 text-[11px] font-semibold hover:bg-primary/20"
                >
                  Download workspace JSON ↓
                </a>
              )}
              <div className="mt-2 flex items-center gap-2 text-muted-foreground">
                <button className="hover:text-foreground">
                  <ThumbsUp className="h-3.5 w-3.5" />
                </button>
                <button className="hover:text-foreground">
                  <ThumbsDown className="h-3.5 w-3.5" />
                </button>
                <button
                  className="hover:text-foreground"
                  onClick={() => navigator.clipboard.writeText(t.content)}
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ),
        )}

        {send.isPending && <p className="text-muted-foreground text-[12px]">Thinking…</p>}
        {send.isError && (
          <div className="rounded-md border border-risk-high/40 bg-risk-high/10 px-3 py-2 text-[12px] text-risk-high">
            {String(send.error)}
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="border-t border-border p-3">
        <form
          onSubmit={submit}
          className="flex items-center gap-2 rounded-md border border-border bg-surface-elevated px-3 py-2 focus-within:border-primary/40"
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={
              workspaceId
                ? activePersona
                  ? `Ask ${activePersona.name}…`
                  : "Ask anything about your data..."
                : "Open a workspace first..."
            }
            disabled={!workspaceId || send.isPending}
            className="flex-1 bg-transparent text-[12px] outline-none placeholder:text-muted-foreground disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={!workspaceId || send.isPending}
            className="grid h-7 w-7 place-items-center rounded bg-primary text-primary-foreground disabled:opacity-50"
          >
            <Send className="h-3.5 w-3.5" />
          </button>
        </form>
        <p className="mt-2 text-center text-[10px] text-muted-foreground">
          Horizon can make mistakes. Validate critical information.
        </p>
      </div>

      {/* Menu overlay */}
      {menuView && workspaceId && (
        <ChatMenu
          view={menuView}
          setView={setMenuView}
          threads={threadsQ.data ?? []}
          threadsLoading={threadsQ.isLoading}
          personas={personasQ.data ?? []}
          activeThreadId={threadId}
          activePersonaId={activePersonaId}
          editingPersona={editingPersona}
          setEditingPersona={setEditingPersona}
          onPickThread={openThread}
          onNewChat={newChat}
          onPickPersona={(pid) => {
            setActivePersonaId(pid);
            setMenuView(null);
          }}
          onDeleteThread={(tid) => deleteThread.mutate(tid)}
          workspaceId={workspaceId}
        />
      )}
    </div>
  );
}

// ---------------- Menu ----------------

function ChatMenu({
  view,
  setView,
  threads,
  threadsLoading,
  personas,
  activeThreadId,
  activePersonaId,
  editingPersona,
  setEditingPersona,
  onPickThread,
  onNewChat,
  onPickPersona,
  onDeleteThread,
  workspaceId,
}: {
  view: MenuView;
  setView: (v: MenuView | null) => void;
  threads: ChatThread[];
  threadsLoading: boolean;
  personas: ChatPersona[];
  activeThreadId: string | null;
  activePersonaId: string | null;
  editingPersona: ChatPersona | null;
  setEditingPersona: (p: ChatPersona | null) => void;
  onPickThread: (t: ChatThread) => void;
  onNewChat: () => void;
  onPickPersona: (pid: string | null) => void;
  onDeleteThread: (tid: string) => void;
  workspaceId: string;
}) {
  return (
    <div className="absolute inset-0 z-30 bg-surface/98 backdrop-blur flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
        <div className="flex items-center gap-2">
          {view !== "main" && (
            <button
              onClick={() => {
                if (view === "persona-edit") {
                  setEditingPersona(null);
                  setView("personas");
                } else {
                  setView("main");
                }
              }}
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
            </button>
          )}
          <span className="text-[13px] font-semibold capitalize">
            {view === "main"
              ? "Menu"
              : view === "personas"
                ? "Lenses"
                : view === "persona-edit"
                  ? editingPersona?.id
                    ? "Edit lens"
                    : "New lens"
                  : view === "history"
                    ? "History"
                    : "Settings"}
          </span>
        </div>
        <button
          onClick={() => setView(null)}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-auto scrollbar-thin">
        {view === "main" && (
          <MenuMain
            threads={threads}
            personas={personas}
            activeThreadId={activeThreadId}
            activePersonaId={activePersonaId}
            onNewChat={onNewChat}
            onPickThread={onPickThread}
            onPickPersona={onPickPersona}
            setView={setView}
          />
        )}
        {/* "Persona" stays in code; user-facing label everywhere is "Lens". */}
        {view === "history" && (
          <ThreadHistory
            threads={threads}
            isLoading={threadsLoading}
            activeThreadId={activeThreadId}
            onPick={onPickThread}
            onDelete={onDeleteThread}
          />
        )}
        {view === "personas" && (
          <PersonaList
            personas={personas}
            activePersonaId={activePersonaId}
            onPick={onPickPersona}
            onEdit={(p) => {
              setEditingPersona(p);
              setView("persona-edit");
            }}
            onNew={() => {
              setEditingPersona(null);
              setView("persona-edit");
            }}
          />
        )}
        {view === "persona-edit" && (
          <PersonaEditor
            initial={editingPersona}
            workspaceId={workspaceId}
            onDone={() => {
              setEditingPersona(null);
              setView("personas");
            }}
          />
        )}
        {view === "settings" && <SettingsPanel />}
      </div>
    </div>
  );
}

function MenuMain({
  threads,
  personas,
  activeThreadId,
  activePersonaId,
  onNewChat,
  onPickThread,
  onPickPersona,
  setView,
}: {
  threads: ChatThread[];
  personas: ChatPersona[];
  activeThreadId: string | null;
  activePersonaId: string | null;
  onNewChat: () => void;
  onPickThread: (t: ChatThread) => void;
  onPickPersona: (pid: string | null) => void;
  setView: (v: MenuView) => void;
}) {
  const recent = threads.slice(0, 3);
  const featured = personas.slice(0, 3);
  return (
    <div className="p-3 space-y-4">
      <button
        onClick={onNewChat}
        className="w-full flex items-center gap-2 rounded-md border border-primary/30 bg-primary/10 text-primary px-3 py-2 text-[12px] font-medium hover:bg-primary/20"
      >
        <Plus className="h-3.5 w-3.5" /> New chat
      </button>

      <Section
        label="History"
        right={
          <button
            onClick={() => setView("history")}
            className="text-[11px] text-primary hover:underline"
          >
            View all
          </button>
        }
      >
        {recent.length === 0 ? (
          <p className="text-[11px] text-muted-foreground italic">
            No prior conversations yet.
          </p>
        ) : (
          recent.map((t) => (
            <button
              key={t.id}
              onClick={() => onPickThread(t)}
              className={`w-full flex items-start gap-2 rounded px-2 py-1.5 text-left transition ${
                activeThreadId === t.id
                  ? "bg-primary/10 text-foreground"
                  : "text-foreground/80 hover:bg-surface-elevated"
              }`}
            >
              <MessageSquare className="h-3 w-3 mt-1 text-muted-foreground shrink-0" />
              <span className="text-[12px] flex-1 truncate">{t.title}</span>
            </button>
          ))
        )}
      </Section>

      <Section
        label="Lenses"
        right={
          <button
            onClick={() => setView("personas")}
            className="text-[11px] text-primary hover:underline"
          >
            View all
          </button>
        }
      >
        <button
          onClick={() => onPickPersona(null)}
          className={`w-full flex items-center gap-2 rounded px-2 py-1.5 text-left text-[12px] transition ${
            !activePersonaId
              ? "bg-primary/10 text-primary"
              : "text-foreground/80 hover:bg-surface-elevated"
          }`}
        >
          <Sparkles className="h-3 w-3" /> None (default)
        </button>
        {featured.map((p) => (
          <button
            key={p.id}
            onClick={() => onPickPersona(p.id)}
            className={`w-full flex items-center gap-2 rounded px-2 py-1.5 text-left text-[12px] transition ${
              activePersonaId === p.id
                ? "bg-primary/10 text-primary"
                : "text-foreground/80 hover:bg-surface-elevated"
            }`}
          >
            <PersonaIcon name={p.icon} className="h-3 w-3" />
            <span className="truncate">{p.name}</span>
          </button>
        ))}
      </Section>

      <button
        onClick={() => setView("settings")}
        className="w-full flex items-center gap-2 rounded px-2 py-1.5 text-left text-[12px] text-foreground/80 hover:bg-surface-elevated"
      >
        <SettingsIcon className="h-3 w-3" /> Settings
      </button>
    </div>
  );
}

function Section({
  label,
  right,
  children,
}: {
  label: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        {right}
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function ThreadHistory({
  threads,
  isLoading,
  activeThreadId,
  onPick,
  onDelete,
}: {
  threads: ChatThread[];
  isLoading: boolean;
  activeThreadId: string | null;
  onPick: (t: ChatThread) => void;
  onDelete: (tid: string) => void;
}) {
  if (isLoading) {
    return (
      <div className="p-4 text-[12px] text-muted-foreground">Loading…</div>
    );
  }
  if (threads.length === 0) {
    return (
      <div className="p-6 text-center">
        <MessageSquare className="h-5 w-5 text-muted-foreground mx-auto mb-2" />
        <p className="text-[12px] text-muted-foreground">
          No conversations yet. Send a message to create one.
        </p>
      </div>
    );
  }
  return (
    <div className="divide-y divide-border">
      {threads.map((t) => (
        <div
          key={t.id}
          className={`flex items-start gap-2 px-3 py-2.5 group ${
            activeThreadId === t.id ? "bg-primary/5" : ""
          }`}
        >
          <button
            onClick={() => onPick(t)}
            className="flex-1 text-left min-w-0"
          >
            <div className="text-[12px] font-medium text-foreground/95 truncate">
              {t.title}
            </div>
            {t.last_message_preview && (
              <div className="text-[11px] text-muted-foreground truncate">
                {t.last_message_preview}
              </div>
            )}
            <div className="text-[10px] text-muted-foreground font-mono mt-0.5">
              {new Date(t.updated_at).toLocaleString()}
              {t.message_count ? ` · ${t.message_count} msgs` : ""}
            </div>
          </button>
          <button
            onClick={() => {
              if (confirm(`Delete "${t.title}"? This cannot be undone.`)) onDelete(t.id);
            }}
            className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-risk-high p-1 transition"
            title="Delete"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      ))}
    </div>
  );
}

function PersonaList({
  personas,
  activePersonaId,
  onPick,
  onEdit,
  onNew,
}: {
  personas: ChatPersona[];
  activePersonaId: string | null;
  onPick: (pid: string | null) => void;
  onEdit: (p: ChatPersona) => void;
  onNew: () => void;
}) {
  return (
    <div className="p-3 space-y-2">
      <p className="text-[11px] text-muted-foreground">
        Lenses change how the AI looks at your workspace — voice, focus, biases.
        Same data, different read. Pick one for this chat, or stay on the default.
      </p>
      <button
        onClick={() => onPick(null)}
        className={`w-full text-left rounded border p-2.5 transition ${
          !activePersonaId
            ? "border-primary/40 bg-primary/10"
            : "border-border hover:border-primary/30 bg-surface-elevated/40"
        }`}
      >
        <div className="flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-[12.5px] font-medium">None (default)</span>
        </div>
        <div className="text-[11px] text-muted-foreground mt-1">
          The base Dravian Horizon assistant.
        </div>
      </button>
      {personas.map((p) => (
        <div
          key={p.id}
          className={`rounded border p-2.5 transition ${
            activePersonaId === p.id
              ? "border-primary/40 bg-primary/10"
              : "border-border bg-surface-elevated/40"
          }`}
        >
          <button onClick={() => onPick(p.id)} className="w-full text-left">
            <div className="flex items-center gap-2">
              <PersonaIcon name={p.icon} className="h-3.5 w-3.5 text-primary" />
              <span className="text-[12.5px] font-medium">{p.name}</span>
              {p.source === "shipped" && (
                <span className="text-[9px] uppercase tracking-wider text-muted-foreground border border-border px-1 py-0.5 rounded">
                  built-in
                </span>
              )}
            </div>
            {p.description && (
              <div className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                {p.description}
              </div>
            )}
          </button>
          <button
            onClick={() => onEdit(p)}
            className="mt-2 text-[10px] text-muted-foreground hover:text-primary"
          >
            Edit instructions →
          </button>
        </div>
      ))}
      <button
        onClick={onNew}
        className="w-full inline-flex items-center justify-center gap-1.5 rounded border border-dashed border-border text-[11px] text-muted-foreground hover:text-primary hover:border-primary/40 py-2"
      >
        <Plus className="h-3 w-3" /> New lens
      </button>
    </div>
  );
}

function PersonaEditor({
  initial,
  workspaceId,
  onDone,
}: {
  initial: ChatPersona | null;
  workspaceId: string;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(initial?.name ?? "");
  const [icon, setIcon] = useState(initial?.icon ?? "sparkles");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [instructions, setInstructions] = useState(initial?.system_instructions ?? "");
  const [starters, setStarters] = useState<string[]>(initial?.starter_prompts ?? []);
  const [newStarter, setNewStarter] = useState("");

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name: name.trim() || "Untitled persona",
        icon,
        description: description.trim() || null,
        system_instructions: instructions,
        starter_prompts: starters,
      };
      if (initial?.id) {
        return personasApi.patch(workspaceId, initial.id, payload);
      }
      return personasApi.create(workspaceId, payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chat-personas", workspaceId] });
      onDone();
    },
  });

  const remove = useMutation({
    mutationFn: () => personasApi.remove(workspaceId, initial!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chat-personas", workspaceId] });
      onDone();
    },
  });

  return (
    <div className="p-3 space-y-3 text-[12px]">
      <Field
        label="Lens name"
        help="Short, recognizable. Shows up in the menu and as the chat header when the lens is active."
      >
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Pessimistic analyst"
          className="w-full bg-surface-elevated border border-border rounded px-2.5 py-1.5 outline-none focus:border-primary/40"
        />
      </Field>
      <Field label="Icon" help="Pick the icon shown next to the name.">
        <div className="flex gap-1.5">
          {["sparkles", "compass", "shield", "lightbulb", "newspaper"].map((opt) => (
            <button
              key={opt}
              onClick={() => setIcon(opt)}
              className={`grid h-8 w-8 place-items-center rounded border transition ${
                icon === opt
                  ? "border-primary/50 bg-primary/15 text-primary"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              <PersonaIcon name={opt} className="h-3.5 w-3.5" />
            </button>
          ))}
        </div>
      </Field>
      <Field
        label="Description"
        help="One sentence: when should I pick this lens? Shows under the name in the menu."
      >
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          placeholder="e.g. Pushes back on every claim, surfaces counter-evidence."
          className="w-full bg-surface-elevated border border-border rounded px-2.5 py-1.5 outline-none focus:border-primary/40 resize-none"
        />
      </Field>
      <Field
        label="System instructions"
        help="Appended to the base system prompt for every turn in chats using this lens. Be specific about voice, focus, biases, and what to never do."
      >
        <textarea
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          rows={6}
          placeholder="Voice: skeptical and direct. For every claim, identify at least one falsifier. Prefer questions over assertions when evidence is thin."
          className="w-full bg-surface-elevated border border-border rounded px-2.5 py-1.5 outline-none focus:border-primary/40 resize-none font-mono text-[11px] leading-relaxed"
        />
      </Field>
      <Field
        label="Starter prompts"
        help="0–5 prompts shown when this lens is freshly picked. Helps the user feel what this lens is good at."
      >
        <div className="space-y-1.5">
          {starters.map((s, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <span className="flex-1 rounded border border-border bg-surface-elevated px-2 py-1 text-[11px]">
                {s}
              </span>
              <button
                onClick={() => setStarters((prev) => prev.filter((_, j) => j !== i))}
                className="text-muted-foreground hover:text-risk-high"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
          {starters.length < 5 && (
            <div className="flex items-center gap-1.5">
              <input
                value={newStarter}
                onChange={(e) => setNewStarter(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newStarter.trim()) {
                    e.preventDefault();
                    setStarters((prev) => [...prev, newStarter.trim()]);
                    setNewStarter("");
                  }
                }}
                placeholder="Type and press Enter…"
                className="flex-1 bg-surface-elevated border border-border rounded px-2 py-1 text-[11px] outline-none focus:border-primary/40"
              />
            </div>
          )}
        </div>
      </Field>

      <div className="flex items-center justify-between pt-3 border-t border-border">
        {initial?.id ? (
          <button
            onClick={() => {
              if (confirm(`Delete lens "${initial.name}"?`)) remove.mutate();
            }}
            className="text-[11px] text-muted-foreground hover:text-risk-high inline-flex items-center gap-1"
          >
            <Trash2 className="h-3 w-3" /> Delete
          </button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">
          <button
            onClick={onDone}
            className="rounded border border-border bg-surface px-2.5 py-1.5 text-[11px] text-muted-foreground hover:text-foreground"
          >
            Cancel
          </button>
          <button
            onClick={() => save.mutate()}
            disabled={save.isPending || !name.trim()}
            className="rounded bg-primary text-primary-foreground px-3 py-1.5 text-[11px] font-semibold disabled:opacity-60"
          >
            {save.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
      {save.isError && (
        <div className="text-[11px] text-risk-high">
          Save failed: {save.error instanceof Error ? save.error.message : "unknown"}
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  help,
  children,
}: {
  label: string;
  help?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
        {label}
      </div>
      {children}
      {help && <p className="text-[10px] text-muted-foreground/80 mt-1 leading-relaxed">{help}</p>}
    </div>
  );
}

function SettingsPanel() {
  return (
    <div className="p-3 space-y-3 text-[12px] text-muted-foreground">
      <p>
        The active LLM provider, API keys, and global system instructions are
        configured per workspace at{" "}
        <span className="text-foreground">Workspace → Settings</span>.
      </p>
      <p>
        Lens instructions live inside each lens — open one from the Lenses list
        to edit its voice and focus.
      </p>
    </div>
  );
}

function PersonaIcon({ name, className }: { name: string; className?: string }) {
  switch (name) {
    case "compass":
      return <Compass className={className} />;
    case "shield":
      return <Shield className={className} />;
    case "lightbulb":
      return <Lightbulb className={className} />;
    case "newspaper":
      return <Newspaper className={className} />;
    default:
      return <Sparkles className={className} />;
  }
}

function summarize(res: ChatResponse): string | undefined {
  const parts: string[] = [];
  if (res.created_entity_ids.length) parts.push(`${res.created_entity_ids.length} entity created`);
  if (res.created_relationship_ids.length)
    parts.push(`${res.created_relationship_ids.length} relationship created`);
  if (res.created_signal_ids.length)
    parts.push(`${res.created_signal_ids.length} signal created`);
  if (res.created_scenario_ids.length)
    parts.push(`${res.created_scenario_ids.length} scenario created`);
  if (res.merged_entity_pairs.length) parts.push(`${res.merged_entity_pairs.length} merged`);
  if (res.recolored_entity_ids.length) parts.push(`${res.recolored_entity_ids.length} recolored`);
  if (res.areas_set_entity_ids.length) parts.push(`${res.areas_set_entity_ids.length} areas`);
  if (res.focus_entity_ids.length) parts.push(`focus on ${res.focus_entity_ids.length}`);
  if (res.deleted_entity_ids.length)
    parts.push(`${res.deleted_entity_ids.length} entity deleted`);
  if (res.deleted_signal_ids?.length)
    parts.push(`${res.deleted_signal_ids.length} signal deleted`);
  if (res.export_url) parts.push("export ready");
  if (res.matched_entity_ids.length) parts.push(`${res.matched_entity_ids.length} matched`);
  if (res.intent !== "modify" && res.intent !== "query" && parts.length === 0)
    return `${res.intent}`;
  return parts.length ? `${res.intent} · ${parts.join(" · ")}` : undefined;
}
